import { useFormContext } from "react-hook-form";
import { Button } from "./Button";
import type { FormButtonProps } from "./Button.types";

// ============================================
// FORM BUTTON COMPONENT
// ============================================

/**
 * FormButton - Button integrated with React Hook Form
 *
 * Automatically handles:
 * - Loading state during form submission
 * - Disabled state when form is invalid
 * - Different text during loading
 *
 * Must be used inside a Form or FormProvider component.
 *
 * @example
 * ```tsx
 * <Form onSubmit={handleSubmit}>
 *   <FormInput name="email" label="Email" />
 *   <FormButton
 *     text="Login"
 *     loadingText="Logging in..."
 *     varient="solid"
 *     color="primary"
 *     size="lg"
 *     fullWidth
 *   />
 * </Form>
 * ```
 */

export function FormButton({
  text,
  loadingText,
  /** Form Behaviour Props */
  disableOnInvalid = true,
  disableOnPristine = false,
  showLoadingOnSubmit = true,
  showLoadingOnValidating = false,

  /** Override Props */
  isLoading: isLoadingProp,
  isDisabled: isDisabledProp,
  /** Button Props */
  type = "submit",
  ...buttonProps
}: FormButtonProps) {
  // ============================================
  // GET FORM CONTEXT
  // ============================================
  /**
   * Why useFormContext?
   * - Gets form state without prop drilling
   * - Automatically updates when form state changes
   * - Works with any form using FormProvider
   */

  const formContext = useFormContext();
  // ============================================
  // HANDLE MISSING CONTEXT
  // ============================================

  /**
   * If FormButton is used outside FormProvider:
   * - Use override props directly
   * - Don't throw error (allows standalone usage)
   */

  if (!formContext) {
    console.warn(
      "FormButton: No FormProvider found. " +
        "Using override props or defaults. " +
        "Wrap your form with <Form> or <FormProvider> for full functionality.",
    );

    const isLoading = isLoadingProp ?? false;
    const isDisabled = isDisabledProp ?? false;
    const displayText = isLoading && loadingText ? loadingText : text;

    return (
      <Button
        type={type}
        isLoading={isLoading}
        isDisabled={isDisabled}
        {...buttonProps}
      >
        {displayText}
      </Button>
    );
  }
  /** Extract Form State */
  const {
    formState: { isSubmitting, isValidating, isValid, isDirty },
  } = formContext;

  // ============================================
  // COMPUTE LOADING STATE
  // ============================================

  /**
   * Loading is true when:
   * 1. Override prop is true (isLoadingProp)
   * 2. Form is submitting AND showLoadingOnSubmit is true
   * 3. Form is validating AND showLoadingOnValidating is true
   */
  const isLoading =
    isLoadingProp ??
    ((showLoadingOnSubmit && isSubmitting) ||
      (showLoadingOnValidating && isValidating));
  // ============================================
  // COMPUTE DISABLED STATE
  // ============================================

  /**
   * Disabled is true when:
   * 1. Override prop is true (isDisabledProp)
   * 2. Button is loading (prevents double-click)
   * 3. Form is invalid AND disableOnInvalid is true
   * 4. Form is pristine AND disableOnPristine is true
   */
  const isDisabled =
    isDisabledProp ??
    (isLoading ||
      (disableOnInvalid && !isValid) ||
      (disableOnPristine && !isDirty));
  // ============================================
  // COMPUTE DISPLAY TEXT
  // ============================================

  /**
   * Show loadingText during loading, otherwise show text
   * If loadingText not provided, keep showing text
   */
  const displayText = isLoading && loadingText ? loadingText : text;
  /** Render */
  return (
    <Button
      type={type}
      isLoading={isLoading}
      isDisabled={isDisabled}
      {...buttonProps}
    >
      {displayText}
    </Button>
  );
}
FormButton.displayName = "FormButton";
